import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Shield, ShieldCheck, AlertTriangle, RefreshCw } from 'lucide-react';
import MFASetupFlow, { MFAEnabledCard, BackupCodesBanner } from './mfa-settings/MFASetupFlow';
import ChangePasswordForm from './mfa-settings/ChangePasswordForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

export default function MFASettingsPage({ user, onLogout }) {
  const [mfaStatus, setMfaStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showMfaPasswords, setShowMfaPasswords] = useState({
    current_password: false,
    new_password: false,
    confirm_password: false
  });

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/mfa/status`, { headers: getAuthHeaders() });
      setMfaStatus(response.data);
    } catch (error) {
      toast.error('Failed to fetch MFA status');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/password/change`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { headers: getAuthHeaders() });
      
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const initiateSetup = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/mfa/setup`, {}, { headers: getAuthHeaders() });
      setSetupData(response.data);
      setBackupCodes(response.data.backup_codes);
      toast.success('Scan the QR code with your authenticator app');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup MFA');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/auth/mfa/verify`, { code: verifyCode }, { headers: getAuthHeaders() });
      toast.success('MFA enabled successfully!');
      setSetupData(null);
      setVerifyCode('');
      setShowBackupCodes(true);
      fetchMFAStatus();

      // If we were in forced enrollment mode, the backend just upgraded our
      // session — swap in the full token and redirect to the dashboard.
      if (resp.data?.upgraded && resp.data?.token) {
        localStorage.setItem('token', resp.data.token);
        if (resp.data.refresh_token) {
          localStorage.setItem('refresh_token', resp.data.refresh_token);
        }
        localStorage.removeItem('mfa_enrollment_required');
        toast.success('Enrollment complete — welcome in!', { duration: 4000 });
        // Small delay so the user sees backup codes if desired
        setTimeout(() => { window.location.href = '/'; }, 1500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const disableMFA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`, { code: disableCode }, { headers: getAuthHeaders() });
      toast.success('MFA disabled');
      setDisableCode('');
      fetchMFAStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const regenerateBackupCodes = async () => {
    const code = prompt('Enter your current MFA code to regenerate backup codes:');
    if (!code) return;
    
    try {
      const response = await axios.post(
        `${API}/auth/mfa/regenerate-backup-codes`,
        { code },
        { headers: getAuthHeaders() }
      );
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodes(true);
      toast.success('New backup codes generated');
      fetchMFAStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate codes');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const downloadBackupCodes = (codes) => {
    const body = [
      'My Shield — two-factor backup codes',
      `Account: ${user?.email || ''}`,
      `Issued: ${new Date().toISOString()}`,
      '',
      'Each code works once. Keep this file somewhere only you can reach.',
      '',
      ...codes,
    ].join('\n');
    const blob = new Blob([body], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'myshield-backup-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Backup codes downloaded');
  };

  const enrollmentRequired = typeof window !== 'undefined' && localStorage.getItem('mfa_enrollment_required') === 'true';

  // Auto-start setup when a privileged user is in enrollment mode and no
  // active setup is loaded yet.
  useEffect(() => {
    if (enrollmentRequired && mfaStatus && !mfaStatus.mfa_enabled && !setupData) {
      initiateSetup();
    }
  }, [enrollmentRequired, mfaStatus, setupData]);

  return (
    <Layout user={user} onLogout={onLogout} activePage="security">
      <div className="max-w-2xl mx-auto space-y-8">
        {enrollmentRequired && !mfaStatus?.mfa_enabled && (
          <div className="rounded-lg border border-amber-400/40 bg-amber-50 p-4 flex items-start gap-3" data-testid="mfa-enrollment-banner">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Two-factor authentication is required.</p>
              <p className="text-sm text-amber-800 mt-1">
                Your role ({user?.role}) requires MFA. Scan the QR code below and enter the 6-digit code to unlock the rest of the app.
              </p>
            </div>
          </div>
        )}
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="mfa-title">
            Two-Factor Authentication
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Add an extra layer of security to your account
          </p>
        </div>

        {/* MFA Status Card */}
        <Card className="border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {mfaStatus?.mfa_enabled ? (
                  <ShieldCheck className="w-6 h-6 text-emerald-600 mr-3" />
                ) : (
                  <Shield className="w-6 h-6 text-slate-400 mr-3" />
                )}
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">
                    {mfaStatus?.mfa_enabled ? 'MFA Enabled' : 'MFA Disabled'}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {mfaStatus?.mfa_enabled 
                      ? (mfaStatus.mfa_enabled_at
                          ? `Enabled on ${new Date(mfaStatus.mfa_enabled_at).toLocaleDateString()}`
                          : 'Enabled')
                      : 'Your account is not protected by two-factor authentication'}
                  </p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                mfaStatus?.mfa_enabled 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {mfaStatus?.mfa_enabled ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div className="p-6">
            {!mfaStatus?.mfa_enabled && !setupData && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Two-factor authentication adds an extra layer of security by requiring a code from your authenticator app in addition to your password.
                </p>
                <Button
                  onClick={initiateSetup}
                  disabled={loading}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="enable-mfa-button"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    <>
                      <Shield className="w-4 h-4 mr-2" />
                      Enable Two-Factor Authentication
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Setup Flow */}
            {setupData && (
              <MFASetupFlow
                setupData={setupData}
                verifyCode={verifyCode}
                onVerifyCodeChange={setVerifyCode}
                onVerify={verifyAndEnable}
                backupCodes={backupCodes}
                loading={loading}
                onCopy={copyToClipboard}
                onDownload={downloadBackupCodes}
              />
            )}

            {/* MFA Enabled State */}
            {mfaStatus?.mfa_enabled && !setupData && (
              <MFAEnabledCard
                mfaStatus={mfaStatus}
                disableCode={disableCode}
                onDisableCodeChange={setDisableCode}
                onDisable={disableMFA}
                onRegenerate={regenerateBackupCodes}
                loading={loading}
              />
            )}
          </div>
        </Card>

        {/* Backup Codes Modal */}
        {showBackupCodes && (
          <BackupCodesBanner
            backupCodes={backupCodes}
            onCopy={copyToClipboard}
            onDownload={downloadBackupCodes}
            onDismiss={() => setShowBackupCodes(false)}
          />
        )}

        {/* Change Password Section */}
        <ChangePasswordForm
          passwordData={passwordData}
          onChange={setPasswordData}
          onSubmit={handleChangePassword}
          changingPassword={changingPassword}
          showPasswords={showMfaPasswords}
          onTogglePassword={setShowMfaPasswords}
        />
      </div>
    </Layout>
  );
}
